import * as THREE from 'three';

import { BodyMarker, CartwheelBoneKey, StepData } from './types';

export const DEMO_ANIMATION_PATH_1 = '/verification-animation-aerobics.bvh';
export const DEMO_ANIMATION_PATH_2 = '/verification-animation-walk-cycle.bvh';
export const DEMO_ANIMATION_PATH_3 = '/verification-animation-tpose.bvh';

export const MODAL_MAX_WIDTH = 1167;

export const CAMERA_POSITION = new THREE.Vector3(0, 0.87, 2.5);

export const AUTORIGGING_STEPS: StepData[] = [
  {
    shortTitle: 'Character orientation',
    title: 'Check character orientation:',
    description:
      'Center and orient your model. Rotate the character to be upright and facing forward. Then drag and place the center line on the middle of the character’s hips.',
    nextButtonText: 'Next',
    previousButtonText: 'Previous',
  },
  {
    shortTitle: 'Place body markers',
    title: 'Drag & Place body markers:',
    description:
      'Drag & place the below joint markers in the designated areas. All joint markers must be placed to continue.',
    nextButtonText: 'Next',
    previousButtonText: 'Previous',
  },
  {
    shortTitle: 'Review animation',
    title: 'Review animation',
    description:
      'Watch the animation to review and verify that your character has been properly rigged. \n\n If everything looks great select “Looks good” to complete the upload. \n\n Otherwise, select “Previous” to go back to marker adjustments.',
    nextButtonText: 'Looks good',
    previousButtonText: 'Previous',
  },
];

export const RETARGETING_STEPS: StepData[] = [
  {
    shortTitle: 'Retarget body bones',
    title: 'Assign bones for retargeting:',
    description:
      'Use the list view below to select and assign your characters bones for retargeting.',
    nextButtonText: 'Next',
    previousButtonText: 'Previous',
  },
  {
    shortTitle: 'Retarget hand bones',
    title: 'Assign bones for retargeting:',
    description:
      'Use the list view below to select and assign your characters bones for retargeting.',
    nextButtonText: 'Next',
    previousButtonText: 'Previous',
  },
  {
    shortTitle: 'Review animation',
    title: 'Review animation',
    description:
      'Play the animation to review and verify that your character has been properly retargeted. \n\n If everything looks great select “Looks good” to complete the upload. \n\n Otherwise, select “Previous” to go back to joint verification.',
    nextButtonText: 'Looks good',
    previousButtonText: 'Previous',
  },
];

export const BONE_DISPLAY_NAMES: Record<CartwheelBoneKey, string> = {
  [CartwheelBoneKey.HEAD]: 'Head',
  [CartwheelBoneKey.PELVIS]: 'Pelvis',
  [CartwheelBoneKey.LEFT_ANKLE]: 'Left Ankle',
  [CartwheelBoneKey.LEFT_KNEE]: 'Left Knee',
  [CartwheelBoneKey.LEFT_WRIST]: 'Left Wrist',
  [CartwheelBoneKey.LEFT_ELBOW]: 'Left Elbow',
  [CartwheelBoneKey.LEFT_SHOULDER]: 'Left Shoulder',
  [CartwheelBoneKey.RIGHT_ANKLE]: 'Right Ankle',
  [CartwheelBoneKey.RIGHT_KNEE]: 'Right Knee',
  [CartwheelBoneKey.RIGHT_WRIST]: 'Right Wrist',
  [CartwheelBoneKey.RIGHT_ELBOW]: 'Right Elbow',
  [CartwheelBoneKey.RIGHT_SHOULDER]: 'Right Shoulder',
  [CartwheelBoneKey.SPINE1]: 'Spine 1',
  [CartwheelBoneKey.SPINE2]: 'Spine 2',
  [CartwheelBoneKey.SPINE3]: 'Spine 3',
  [CartwheelBoneKey.NECK]: 'Neck',
  [CartwheelBoneKey.LEFT_COLLAR]: 'Left Collar',
  [CartwheelBoneKey.RIGHT_COLLAR]: 'Right Collar',
  [CartwheelBoneKey.LEFT_THUMB1]: 'Left Thumb 1',
  [CartwheelBoneKey.LEFT_THUMB2]: 'Left Thumb 2',
  [CartwheelBoneKey.LEFT_THUMB3]: 'Left Thumb 3',
  [CartwheelBoneKey.RIGHT_THUMB1]: 'Right Thumb 1',
  [CartwheelBoneKey.RIGHT_THUMB2]: 'Right Thumb 2',
  [CartwheelBoneKey.RIGHT_THUMB3]: 'Right Thumb 3',
  [CartwheelBoneKey.LEFT_INDEX1]: 'Left Index 1',
  [CartwheelBoneKey.LEFT_INDEX2]: 'Left Index 2',
  [CartwheelBoneKey.LEFT_INDEX3]: 'Left Index 3',
  [CartwheelBoneKey.RIGHT_INDEX1]: 'Right Index 1',
  [CartwheelBoneKey.RIGHT_INDEX2]: 'Right Index 2',
  [CartwheelBoneKey.RIGHT_INDEX3]: 'Right Index 3',
  [CartwheelBoneKey.LEFT_MIDDLE1]: 'Left Middle 1',
  [CartwheelBoneKey.LEFT_MIDDLE2]: 'Left Middle 2',
  [CartwheelBoneKey.LEFT_MIDDLE3]: 'Left Middle 3',
  [CartwheelBoneKey.RIGHT_MIDDLE1]: 'Right Middle 1',
  [CartwheelBoneKey.RIGHT_MIDDLE2]: 'Right Middle 2',
  [CartwheelBoneKey.RIGHT_MIDDLE3]: 'Right Middle 3',
  [CartwheelBoneKey.LEFT_RING1]: 'Left Ring 1',
  [CartwheelBoneKey.LEFT_RING2]: 'Left Ring 2',
  [CartwheelBoneKey.LEFT_RING3]: 'Left Ring 3',
  [CartwheelBoneKey.RIGHT_RING1]: 'Right Ring 1',
  [CartwheelBoneKey.RIGHT_RING2]: 'Right Ring 2',
  [CartwheelBoneKey.RIGHT_RING3]: 'Right Ring 3',
  [CartwheelBoneKey.LEFT_PINKY1]: 'Left Pinky 1',
  [CartwheelBoneKey.LEFT_PINKY2]: 'Left Pinky 2',
  [CartwheelBoneKey.LEFT_PINKY3]: 'Left Pinky 3',
  [CartwheelBoneKey.RIGHT_PINKY1]: 'Right Pinky 1',
  [CartwheelBoneKey.RIGHT_PINKY2]: 'Right Pinky 2',
  [CartwheelBoneKey.RIGHT_PINKY3]: 'Right Pinky 3',
  [CartwheelBoneKey.LEFT_HIP]: 'Left Hip',
  [CartwheelBoneKey.LEFT_FOOT]: 'Left Foot',
  [CartwheelBoneKey.RIGHT_HIP]: 'Right Hip',
  [CartwheelBoneKey.RIGHT_FOOT]: 'Right Foot',
};

export const BODY_BONES = [
  CartwheelBoneKey.HEAD,
  CartwheelBoneKey.PELVIS,
  CartwheelBoneKey.LEFT_ANKLE,
  CartwheelBoneKey.LEFT_KNEE,
  CartwheelBoneKey.LEFT_WRIST,
  CartwheelBoneKey.LEFT_ELBOW,
  CartwheelBoneKey.LEFT_SHOULDER,
  CartwheelBoneKey.RIGHT_ANKLE,
  CartwheelBoneKey.RIGHT_KNEE,
  CartwheelBoneKey.RIGHT_WRIST,
  CartwheelBoneKey.RIGHT_ELBOW,
  CartwheelBoneKey.RIGHT_SHOULDER,
  CartwheelBoneKey.SPINE1,
  CartwheelBoneKey.SPINE2,
  CartwheelBoneKey.SPINE3,
  CartwheelBoneKey.NECK,
  CartwheelBoneKey.LEFT_COLLAR,
  CartwheelBoneKey.RIGHT_COLLAR,
  CartwheelBoneKey.LEFT_HIP,
  CartwheelBoneKey.LEFT_FOOT,
  CartwheelBoneKey.RIGHT_HIP,
  CartwheelBoneKey.RIGHT_FOOT,
];

export const HAND_BONES = [
  CartwheelBoneKey.LEFT_THUMB1,
  CartwheelBoneKey.LEFT_THUMB2,
  CartwheelBoneKey.LEFT_THUMB3,
  CartwheelBoneKey.RIGHT_THUMB1,
  CartwheelBoneKey.RIGHT_THUMB2,
  CartwheelBoneKey.RIGHT_THUMB3,
  CartwheelBoneKey.LEFT_INDEX1,
  CartwheelBoneKey.LEFT_INDEX2,
  CartwheelBoneKey.LEFT_INDEX3,
  CartwheelBoneKey.RIGHT_INDEX1,
  CartwheelBoneKey.RIGHT_INDEX2,
  CartwheelBoneKey.RIGHT_INDEX3,
  CartwheelBoneKey.LEFT_MIDDLE1,
  CartwheelBoneKey.LEFT_MIDDLE2,
  CartwheelBoneKey.LEFT_MIDDLE3,
  CartwheelBoneKey.RIGHT_MIDDLE1,
  CartwheelBoneKey.RIGHT_MIDDLE2,
  CartwheelBoneKey.RIGHT_MIDDLE3,
  CartwheelBoneKey.LEFT_RING1,
  CartwheelBoneKey.LEFT_RING2,
  CartwheelBoneKey.LEFT_RING3,
  CartwheelBoneKey.RIGHT_RING1,
  CartwheelBoneKey.RIGHT_RING2,
  CartwheelBoneKey.RIGHT_RING3,
  CartwheelBoneKey.LEFT_PINKY1,
  CartwheelBoneKey.LEFT_PINKY2,
  CartwheelBoneKey.LEFT_PINKY3,
  CartwheelBoneKey.RIGHT_PINKY1,
  CartwheelBoneKey.RIGHT_PINKY2,
  CartwheelBoneKey.RIGHT_PINKY3,
];

export const MARKERS: BodyMarker[] = [
  {
    name: 'Head',
    color: '#1478FF',
    markers: [
      {
        id: CartwheelBoneKey.HEAD,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.HEAD],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
    ],
  },
  {
    name: 'Wrists',
    color: '#0FD23A',
    markers: [
      {
        id: CartwheelBoneKey.LEFT_WRIST,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.LEFT_WRIST],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
      {
        id: CartwheelBoneKey.RIGHT_WRIST,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.RIGHT_WRIST],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
    ],
  },
  {
    name: 'Shoulders',
    color: '#FF3F56',
    markers: [
      {
        id: CartwheelBoneKey.LEFT_SHOULDER,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.LEFT_SHOULDER],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
      {
        id: CartwheelBoneKey.RIGHT_SHOULDER,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.RIGHT_SHOULDER],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
    ],
  },
  {
    name: 'Elbows',
    color: '#5900F5',
    markers: [
      {
        id: CartwheelBoneKey.LEFT_ELBOW,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.LEFT_ELBOW],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
      {
        id: CartwheelBoneKey.RIGHT_ELBOW,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.RIGHT_ELBOW],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
    ],
  },
  {
    name: 'Pelvis',
    color: '#FFD500',
    markers: [
      {
        id: CartwheelBoneKey.PELVIS,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.PELVIS],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
    ],
  },
  {
    name: 'Knees',
    color: '#DFA982',
    markers: [
      {
        id: CartwheelBoneKey.LEFT_KNEE,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.LEFT_KNEE],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
      {
        id: CartwheelBoneKey.RIGHT_KNEE,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.RIGHT_KNEE],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
    ],
  },
  {
    name: 'Ankles',
    color: '#E739E8',
    markers: [
      {
        id: CartwheelBoneKey.LEFT_ANKLE,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.LEFT_ANKLE],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
      {
        id: CartwheelBoneKey.RIGHT_ANKLE,
        name: BONE_DISPLAY_NAMES[CartwheelBoneKey.RIGHT_ANKLE],
        worldPosition: undefined,
        screenPosition: undefined,
        isDragging: false,
        isError: false,
      },
    ],
  },
];

// Constants for rotation values
export const ROTATION_LIMITS = {
  X_HEAD: {
    MIN: -45,
    MAX: 45,
  },
  Z_COLLAR: {
    MIN: -45,
    MAX: 45,
  },
  Z_HIP: {
    MIN: -30,
    MAX: 30,
  },
  Z_SHOULDER: {
    MIN: -60,
    MAX: 60,
  },
  Y_ANKLE: {
    MIN: -60,
    MAX: 60,
  },
};
